/**
 * Shared schedule drag action helpers.
 *
 * Single source of truth for the *business logic* layer of drag-and-drop
 * actions on the Schedule (Calendar + Team views). The view-specific
 * drag mechanics (FullCalendar timegrid for Calendar, custom mouse drag for
 * Team) live in their own files, but every decision that should be
 * *identical* between the two views — toast wording, conflict message
 * composition, error mapping — happens here.
 *
 * Note: there is no "resize" action — duration changes go through the
 * Visit Details drawer form, not through a calendar resize handle.
 *
 * Why this exists
 * ---------------
 * Calendar and Team historically grew two parallel drag implementations.
 * They diverged subtly: Calendar's "Failed to move" was actually
 * "An unexpected error occurred"; Team's conflict-message builder was a
 * copy-paste of Calendar's; etc. This module collapses those divergences
 * by making the relevant logic pure functions that both views import.
 *
 * Public API
 * ----------
 * - `guardDragIntent({ event, targetDate, now? })`
 *     Validates a drag intent before the API call. Returns either
 *     `{ allowed: true }` or `{ allowed: false, reason, silent?, toastKey? }`.
 *     The caller decides how to react: revert, optionally show a toast.
 *     Encodes both the silent guards (event not legitimately draggable —
 *     overdue, status terminal) and the visible past-date guard. New
 *     business rules (no-drag-on-holidays, etc.) add branches here.
 *
 * - `performDrag(intent, opts?)` → Promise<{ data }>
 *     Executes the drag against the API. Hides the two endpoints
 *     (/move/ and /update-assignment/), the CSRF/header boilerplate, the
 *     date+cleaner cascade for Team, and the WS-refresh suppression hook.
 *     Rejects with `{ isConflict, data }` on 409 (Team-shape) or with the
 *     raw API payload on other 4xx (Calendar-shape with `{error, conflicts}`).
 *     Both error shapes are understood by `toastFromDragError`.
 *
 * - `composeConflictMessage(conflicts, T)`
 *     Builds the multi-line "Conflicts detected:\n• Bob: 30 min overlap\n…"
 *     toast string from a backend conflicts array.
 *
 * - `toastFromDragError(error, action, T)`
 *     Maps an API/network error to the user-facing toast string. Accepts
 *     errors in both shapes that the two views currently throw:
 *       - Calendar `handleApiError` shape: `{ error?: string, conflicts?: [] }`
 *       - Team inline-catch shape:         `{ isConflict?: bool, data?: { conflicts: [] } }`
 *     `action` selects the action-specific fallback wording when the error
 *     carries no useful detail.
 *
 * Both helpers accept a translations object `T` (typically
 * `window.SCHEDULE_TRANSLATIONS`). They never touch the DOM; they never
 * call `showToast`. The caller decides how/whether to display the result.
 * This keeps them trivially unit-testable from Node.
 */

/**
 * Validate a drag intent against the business rules of the Schedule.
 *
 * Two tiers of checks:
 *   1. Silent guards — the event was never legitimately draggable in the
 *      first place (overdue, terminal status). The card style already
 *      conveys non-interactivity (cursor-pointer instead of cursor-grab),
 *      so a toast on every refused drag would be pure noise. The view
 *      should revert silently.
 *   2. Visible guards — the drag itself was legitimate but the chosen
 *      target is invalid (past date today). The view should revert and
 *      show a toast so the snap-back has an explanation.
 *
 * Either tier may grow new branches over time (e.g. blacklisted slots,
 * holidays, locked weeks). Adding a new rule = one branch here.
 *
 * @param {Object} args
 * @param {Object} [args.event]    Event extended props (status, is_overdue, …).
 *                                 Optional: Team strips this at startDragVisuals
 *                                 time, so silent guards are already enforced
 *                                 before performDrop is called.
 * @param {Date|string} args.targetDate  The day the user dropped onto.
 * @param {Date} [args.now]        Override "now" (for tests).
 * @returns {{ allowed: boolean, reason?: string, silent?: boolean, toastKey?: string }}
 */
export function guardDragIntent({ event, targetDate, now } = {}) {
    if (event) {
        if (event.is_overdue) {
            return { allowed: false, reason: 'overdue', silent: true };
        }
        if (event.status && event.status !== 'scheduled') {
            return { allowed: false, reason: 'not_scheduled', silent: true };
        }
        // Externally-handled visits are non-draggable: the informational
        // subcontractor agent is changed via the drawer, never by
        // dragging onto one of our agents' lanes.
        if (event.is_externally_handled) {
            return { allowed: false, reason: 'externally_handled', silent: true };
        }
    }

    const today = new Date(now || new Date());
    today.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    if (target < today) {
        return {
            allowed: false,
            reason: 'past',
            toastKey: 'cannotScheduleInPast',
        };
    }

    return { allowed: true };
}

/**
 * Compose a multi-line toast message from a list of conflict objects.
 *
 * @param {Array<{cleaner_name: string, overlap_minutes: number}>} conflicts
 * @param {Object} [T] Translations map (window.SCHEDULE_TRANSLATIONS shape).
 * @returns {string} Toast-ready string with header + bullet per conflict.
 *                   Always trailing newline after the last bullet.
 */
export function composeConflictMessage(conflicts, T = {}) {
    const header = T.conflictsDetected || 'Conflicts detected:';
    const minOverlap = T.minOverlap || 'min overlap';
    let msg = header + '\n';
    if (Array.isArray(conflicts)) {
        for (const c of conflicts) {
            msg += `• ${c.cleaner_name}: ${c.overlap_minutes} ${minOverlap}\n`;
        }
    }
    return msg;
}

/**
 * Map a drag API error to a user-facing toast message.
 *
 * Order of precedence:
 *   1. Team-shape conflict   → composeConflictMessage(error.data.conflicts)
 *   2. Calendar-shape conflict → composeConflictMessage(error.conflicts)
 *   3. Server-provided `error.error` string → returned as-is
 *   4. Action-specific fallback (T.failedToMove / T.failedToReassign)
 *
 * @param {Object|null|undefined} error  The error caught from the API call.
 * @param {'move'|'reassign'} [action='move']  What the user was trying to do.
 * @param {Object} [T]  Translations map.
 * @returns {string}  Toast-ready string.
 */
export function toastFromDragError(error, action = 'move', T = {}) {
    if (error) {
        // Team-shape: error was thrown as { isConflict: true, data: { conflicts: [...] } }
        if (error.isConflict && error.data && Array.isArray(error.data.conflicts)) {
            return composeConflictMessage(error.data.conflicts, T);
        }
        // Calendar-shape: error was thrown as the API payload directly,
        // which carries `conflicts` alongside `error`.
        if (Array.isArray(error.conflicts)) {
            return composeConflictMessage(error.conflicts, T);
        }
        // Server-provided detail string (non-conflict 4xx/5xx with payload).
        if (typeof error.error === 'string' && error.error.length > 0) {
            return error.error;
        }
    }
    return fallbackForAction(action, T);
}

function fallbackForAction(action, T) {
    switch (action) {
        case 'reassign':
            return T.failedToReassign || 'Failed to reassign visit';
        case 'move':
        default:
            return T.failedToMove || 'Failed to move visit';
    }
}

// ---------------------------------------------------------------------------
// performDrag — orchestrator
// ---------------------------------------------------------------------------

const MOVE_ENDPOINT = (id) => `/office/api/schedule/occurrence/${id}/move/`;
const REASSIGN_ENDPOINT = (id) => `/office/visits/${id}/update-assignment/`;

/**
 * Execute a drag intent against the API.
 *
 * The intent describes what changed; this helper picks the right endpoint(s)
 * and chains them in the correct order (reassign first, move second when both
 * apply — the move/ endpoint validates conflicts against the *new* cleaner).
 *
 * @param {Object} intent
 * @param {string|number} intent.visitId
 * @param {Date|string} [intent.newStart]      ISO/Date — required if dateChanged.
 * @param {Date|string} [intent.newEnd]        ISO/Date — required if dateChanged.
 * @param {number}      [intent.durationMinutes] Required if dateChanged.
 * @param {string|number} [intent.newCleanerId]  Required if cleanerChanged.
 * @param {boolean}     [intent.dateChanged]    Defaults to false.
 * @param {boolean}     [intent.cleanerChanged] Defaults to false.
 *
 * @param {Object} [opts]
 * @param {string|null} [opts.csrfToken]       Falls back to reading from cookies.
 * @param {Function}    [opts.fetch]           Inject fetch (for tests). Defaults to global.
 * @param {Function}    [opts.skipNextWsRefresh] Optional hook called once before
 *                                                the fetch chain. Used by Team to
 *                                                suppress its own brutal full-refresh
 *                                                broadcast over WebSocket. Calendar
 *                                                doesn't pass it — its own refresh
 *                                                callback (FullCalendar refetchEvents)
 *                                                is smooth and doesn't need suppressing.
 *
 * @returns {Promise<{data: Object|null}>}
 *   Resolves with the move/ API JSON response (carries `classNames`,
 *   `affected_event_ids`, `has_conflicts`) when a move ran. Resolves with
 *   `{ data: null }` when only a reassign ran (or nothing changed). Rejects
 *   with `{ isConflict: true, data: payload }` on 409, or with the raw
 *   payload on other 4xx — both shapes flow into `toastFromDragError`.
 */
export async function performDrag(intent, opts = {}) {
    const fetchImpl = opts.fetch || (typeof fetch !== 'undefined' ? fetch : null);
    if (!fetchImpl) {
        throw new Error('performDrag: no fetch implementation available');
    }
    const csrfToken =
        opts.csrfToken !== undefined ? opts.csrfToken : readCsrfFromCookies();

    const {
        visitId,
        newStart,
        newEnd,
        durationMinutes,
        newCleanerId,
        oldCleanerId,
        dateChanged,
        cleanerChanged,
    } = intent;

    if (!dateChanged && !cleanerChanged) {
        return { data: null };
    }

    if (typeof opts.skipNextWsRefresh === 'function') {
        opts.skipNextWsRefresh();
    }

    if (cleanerChanged) {
        await postReassign(fetchImpl, visitId, newCleanerId, csrfToken, oldCleanerId);
    }

    if (dateChanged) {
        const data = await postMove(
            fetchImpl,
            visitId,
            newStart,
            newEnd,
            durationMinutes,
            csrfToken,
        );
        return { data };
    }

    return { data: null };
}

async function postMove(fetchImpl, visitId, newStart, newEnd, durationMinutes, csrfToken) {
    const response = await fetchImpl(MOVE_ENDPOINT(visitId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
            start: toIso(newStart),
            end: toIso(newEnd),
            duration_minutes: durationMinutes,
        }),
    });
    if (!response.ok) {
        // Try to parse the JSON payload for a structured error. If parsing
        // fails (HTML 500 page, network blip), throw an empty object — the
        // toast layer will fall back to the action-specific generic wording.
        let payload = {};
        try {
            payload = await response.json();
        } catch {
            // intentionally swallowed
        }
        if (response.status === 409) {
            throw { isConflict: true, data: payload };
        }
        throw payload;
    }
    return response.json();
}

async function postReassign(fetchImpl, visitId, cleanerId, csrfToken, oldCleanerId) {
    // Targeted swap = move ONE cleaner without disturbing the rest of the
    // roster (``[A,B,C] → [D,B,C]`` when dragging A onto D's lane). The
    // endpoint's ``add_cleaner`` / ``remove_cleaner`` mode (assignments.py)
    // keeps the others intact and stays idempotent. When the bar came from the
    // Unassigned lane there is no ``oldCleanerId`` → pure add. When neither the
    // endpoint nor the caller knows a previous cleaner (legacy single-cleaner
    // full-replace), fall back to ``assigned_employees`` = the new sole
    // cleaner so a one-cleaner reassign still works unchanged.
    let body;
    if (oldCleanerId !== undefined && oldCleanerId !== null && String(oldCleanerId) !== '') {
        body = `add_cleaner=${encodeURIComponent(cleanerId)}&remove_cleaner=${encodeURIComponent(oldCleanerId)}`;
    } else if (cleanerId !== undefined && cleanerId !== null) {
        // Pure add (drag from Unassigned). Targeted add preserves any cleaners
        // already on the visit; ``assigned_employees`` would wipe them.
        body = `add_cleaner=${encodeURIComponent(cleanerId)}`;
    } else {
        body = `assigned_employees=${encodeURIComponent(cleanerId)}`;
    }
    const response = await fetchImpl(REASSIGN_ENDPOINT(visitId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-CSRFToken': csrfToken,
        },
        body,
    });
    if (!response.ok && !response.redirected) {
        // No structured payload contract on this endpoint — throw a minimal
        // shape so toastFromDragError falls back to T.failedToReassign.
        throw {};
    }
    return null;
}

function toIso(d) {
    if (d instanceof Date) return d.toISOString();
    return d;
}

function readCsrfFromCookies() {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const c = cookie.trim();
        if (c.startsWith('csrftoken=')) {
            return decodeURIComponent(c.substring(10));
        }
    }
    return null;
}
